/**
 * The tracks a cycle-level guided path writes to (the cycle configure wizard):
 * the cycle's open tracks, and which of them the person has left ticked.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTrackStore } from '../../stores/prioritization/trackStore';
import { isOpenTrack } from '../../lib/poolScope';
import type { Track } from '../../types/prioritization/track';
import type { WizardTrackTarget } from '../../lib/cycleWizardTargets';

/** The cycle's open tracks, read from the track store and asked for on mount. */
export function useCycleOpenTracks(cycleId: string): { tracks: Track[]; refresh: () => Promise<void> } {
  const allTracks = useTrackStore((s) => s.tracks);
  const fetchTracksForCycle = useTrackStore((s) => s.fetchTracksForCycle);
  const refresh = useCallback(() => fetchTracksForCycle(cycleId), [cycleId, fetchTracksForCycle]);
  useEffect(() => {
    void refresh();
  }, [refresh]);
  const tracks = useMemo(
    () =>
      allTracks
        .filter((t) => t.cycleId === cycleId && isOpenTrack(t))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [allTracks, cycleId],
  );
  return { tracks, refresh };
}

/** Which writable tracks are ticked: all of them, less the ones the person unticked. */
export function useChosenTracks(targets: readonly WizardTrackTarget[]): {
  chosenIds: string[];
  toggle: (trackId: string) => void;
} {
  const [unticked, setUnticked] = useState<ReadonlySet<string>>(new Set());
  const chosenIds = useMemo(
    () => targets.filter((t) => t.writable && !unticked.has(t.trackId)).map((t) => t.trackId),
    [targets, unticked],
  );
  const toggle = useCallback((trackId: string) => {
    setUnticked((prev) => {
      const next = new Set(prev);
      if (next.has(trackId)) next.delete(trackId);
      else next.add(trackId);
      return next;
    });
  }, []);
  return { chosenIds, toggle };
}
